import { useState } from 'react';
import { Outlet, NavLink, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import {
  LayoutDashboard,
  Monitor,
  Megaphone,
  ImageIcon,
  Calendar,
  DollarSign,
  Users,
  Bell,
  Settings,
  LogOut,
  Menu,
  X,
  ChevronDown,
  Layers,
  CheckCircle2,
  Wifi,
  BarChart3,
  Receipt,
  LayoutTemplate,
  Lightbulb,
  Radio,
  CalendarClock,
  Handshake,
  Palette,
  Activity,
  UserCheck,
  ShieldAlert,
  Building2,
  MonitorPlay,
  Smartphone,
  Plug,
  Film,
  PlayCircle,
  Server,
  PieChart,
} from 'lucide-react';
import { Button } from './ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import { Avatar, AvatarFallback } from './ui/avatar';
import { Badge } from './ui/badge';

const navigation = [
  { name: 'Dashboard', href: '/dashboard', icon: LayoutDashboard, roles: ['admin', 'operator', 'client'], permission: 'dashboard' },
  { name: 'Screens', href: '/screens', icon: Monitor, roles: ['admin', 'operator', 'client'], permission: 'screens' },
  { name: 'Screen Connect', href: '/screen-connection', icon: Wifi, roles: ['admin', 'operator'], permission: 'screen_connect' },
  { name: 'Campaigns', href: '/campaigns', icon: Megaphone, roles: ['admin', 'operator', 'client'], permission: 'campaigns' },
  { name: 'Content Library', href: '/content', icon: ImageIcon, roles: ['admin', 'operator', 'client'], permission: 'content' },
  { name: 'Templates', href: '/templates', icon: LayoutTemplate, roles: ['admin', 'operator', 'client'], permission: 'templates' },
  { name: 'Content Assign', href: '/content-assignment', icon: Layers, roles: ['admin', 'operator'], permission: 'content_assign' },
  { name: 'Content Review', href: '/content-approval', icon: CheckCircle2, roles: ['admin', 'operator'], permission: 'content_review' },
  // Ad Server Section
  { name: 'Creatives', href: '/creatives', icon: Film, roles: ['admin', 'operator'], permission: 'content' },
  { name: 'Player Campaigns', href: '/player-campaigns', icon: PlayCircle, roles: ['admin', 'operator'], permission: 'campaigns' },
  { name: 'Ad Server', href: '/ad-server', icon: Server, roles: ['admin', 'operator'], permission: 'programmatic' },
  { name: 'Player Reports', href: '/player-reports', icon: PieChart, roles: ['admin', 'operator'], permission: 'analytics' },
  // Scheduling
  { name: 'Schedule', href: '/schedule', icon: Calendar, roles: ['admin', 'operator'], permission: 'schedule' },
  { name: 'Adv. Scheduling', href: '/advanced-scheduling', icon: CalendarClock, roles: ['admin', 'operator'], permission: 'advanced_scheduling' },
  { name: 'Analytics', href: '/analytics', icon: BarChart3, roles: ['admin', 'operator', 'client'], permission: 'analytics' },
  { name: 'RTB Analytics', href: '/rtb-analytics', icon: Activity, roles: ['admin', 'operator'], permission: 'rtb_analytics' },
  { name: 'Audience', href: '/audience-analytics', icon: UserCheck, roles: ['admin', 'operator'], permission: 'audience' },
  { name: 'Device Capture', href: '/device-capture', icon: Smartphone, roles: ['admin', 'operator'], permission: 'audience' },
  { name: 'Recommendations', href: '/recommendations', icon: Lightbulb, roles: ['admin', 'operator'], permission: 'recommendations' },
  { name: 'Programmatic', href: '/programmatic', icon: Radio, roles: ['admin', 'operator'], permission: 'programmatic' },
  { name: 'DSP Partners', href: '/dsp-partners', icon: Handshake, roles: ['admin'], permission: 'dsp_partners' },
  { name: 'DV360', href: '/dv360', icon: MonitorPlay, roles: ['admin', 'operator'], permission: 'dsp_partners' },
  { name: 'Billing', href: '/billing', icon: DollarSign, roles: ['admin', 'client'], permission: 'billing' },
  { name: 'Client Billing', href: '/client-billing', icon: Receipt, roles: ['admin', 'operator'], permission: 'usage_billing' },
  { name: 'Usage Billing', href: '/usage-billing', icon: Receipt, roles: ['admin', 'operator'], permission: 'usage_billing' },
  { name: 'White Label', href: '/white-label', icon: Palette, roles: ['admin'], permission: 'white_label' },
  { name: 'Clients', href: '/clients', icon: Building2, roles: ['admin', 'operator'], permission: 'users' },
  { name: 'Users', href: '/users', icon: Users, roles: ['admin'], permission: 'users' },
  { name: 'Audit Logs', href: '/audit-logs', icon: ShieldAlert, roles: ['admin'], permission: 'users' },
  { name: 'Integrations', href: '/integrations', icon: Plug, roles: ['admin'], permission: 'users' },
];

export default function Layout() {
  const { user, logout, hasPermission } = useAuth();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  // Filter navigation based on role AND permissions
  const filteredNavigation = navigation.filter(item => {
    // Admin always sees everything
    if (user?.role === 'admin') return true;
    
    // Check role-based access first
    if (!item.roles.includes(user?.role)) return false;
    
    // Then check permissions (if user has permissions array)
    if (user?.permissions && user.permissions.length > 0) {
      // If 'all' permission, show everything for this role
      if (user.permissions.includes('all')) return true;
      // Otherwise check specific permission
      return hasPermission(item.permission);
    }
    
    // If no permissions set, fall back to role-based
    return true;
  });

  const getRoleBadgeColor = (role) => {
    switch (role) {
      case 'admin': return 'bg-purple-500/10 text-purple-400 border-purple-500/20';
      case 'operator': return 'bg-blue-500/10 text-blue-400 border-blue-500/20';
      case 'client': return 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20';
      default: return 'bg-zinc-500/10 text-zinc-400 border-zinc-500/20';
    }
  };

  return (
    <div className="min-h-screen bg-zinc-950">
      {/* Mobile sidebar backdrop */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 bg-black/60 backdrop-blur-sm z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside className={`fixed left-0 top-0 z-50 h-screen w-64 bg-zinc-950 border-r border-zinc-800/50 transform transition-transform duration-300 lg:translate-x-0 flex flex-col ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}`}>
        {/* Logo */}
        <div className="flex items-center justify-between h-16 px-6 border-b border-zinc-800/50 flex-shrink-0">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-lg bg-blue-600 flex items-center justify-center">
              <span className="text-white font-bold text-sm" style={{ fontFamily: 'Space Grotesk' }}>L</span>
            </div>
            <span className="text-lg font-semibold text-white" style={{ fontFamily: 'Space Grotesk' }}>Lumina OS</span>
          </div>
          <button 
            className="lg:hidden text-zinc-400 hover:text-white"
            onClick={() => setSidebarOpen(false)}
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Navigation - Scrollable */}
        <nav className="flex-1 px-3 py-4 space-y-1 overflow-y-auto scrollbar-thin scrollbar-thumb-zinc-700 scrollbar-track-transparent">
          {filteredNavigation.map((item) => (
            <NavLink
              key={item.name}
              to={item.href}
              onClick={() => setSidebarOpen(false)}
              className={({ isActive }) =>
                `flex items-center gap-3 px-4 py-3 rounded-lg transition-all duration-200 ${
                  isActive
                    ? 'bg-blue-600/10 text-blue-500 border border-blue-500/20'
                    : 'text-zinc-400 hover:text-white hover:bg-zinc-800/50'
                }`
              }
              data-testid={`nav-${item.name.toLowerCase().replace(' ', '-')}`}
            >
              <item.icon className="w-5 h-5" strokeWidth={1.5} />
              <span className="font-medium">{item.name}</span>
            </NavLink>
          ))}
        </nav>

        {/* User section - Fixed at bottom */}
        <div className="p-4 border-t border-zinc-800/50 flex-shrink-0">
          <div className="flex items-center gap-3 px-2">
            <Avatar className="w-10 h-10">
              <AvatarFallback className="bg-zinc-800 text-zinc-300">
                {user?.name?.charAt(0).toUpperCase()}
              </AvatarFallback>
            </Avatar>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-white truncate">{user?.name}</p>
              <Badge variant="outline" className={`text-[10px] mt-0.5 ${getRoleBadgeColor(user?.role)}`}>
                {user?.role}
              </Badge>
            </div>
          </div>
        </div>
      </aside>

      {/* Main content */}
      <div className="lg:ml-64">
        {/* Header */}
        <header className="sticky top-0 z-30 backdrop-blur-xl bg-zinc-950/80 border-b border-zinc-800/50">
          <div className="flex items-center justify-between h-16 px-4 lg:px-6">
            <button 
              className="lg:hidden p-2 text-zinc-400 hover:text-white"
              onClick={() => setSidebarOpen(true)}
              data-testid="mobile-menu-btn"
            >
              <Menu className="w-6 h-6" />
            </button>

            <div className="flex-1" />

            <div className="flex items-center gap-2">
              <NavLink to="/notifications">
                <Button variant="ghost" size="icon" className="relative" data-testid="notifications-btn">
                  <Bell className="w-5 h-5 text-zinc-400" />
                  <span className="absolute top-1.5 right-1.5 w-2 h-2 bg-blue-500 rounded-full" />
                </Button>
              </NavLink>

              <NavLink to="/settings">
                <Button variant="ghost" size="icon" data-testid="settings-btn">
                  <Settings className="w-5 h-5 text-zinc-400" />
                </Button>
              </NavLink>

              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" className="flex items-center gap-2 ml-2" data-testid="user-menu-btn">
                    <Avatar className="w-8 h-8">
                      <AvatarFallback className="bg-zinc-800 text-zinc-300 text-sm">
                        {user?.name?.charAt(0).toUpperCase()}
                      </AvatarFallback>
                    </Avatar>
                    <ChevronDown className="w-4 h-4 text-zinc-400" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56 bg-zinc-900 border-zinc-800">
                  <div className="px-3 py-2">
                    <p className="text-sm font-medium text-white">{user?.name}</p>
                    <p className="text-xs text-zinc-500">{user?.email}</p>
                  </div>
                  <DropdownMenuSeparator className="bg-zinc-800" />
                  <DropdownMenuItem 
                    onClick={handleLogout}
                    className="text-rose-400 focus:text-rose-400 focus:bg-rose-500/10 cursor-pointer"
                    data-testid="logout-btn"
                  >
                    <LogOut className="w-4 h-4 mr-2" />
                    Sign out
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        </header>

        {/* Page content */}
        <main className="p-4 lg:p-6">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
